import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Building2, MapPin, Calendar, ArrowRight, 
  CheckCircle2, Users, Target, Award, X 
} from 'lucide-react';
import PropTypes from 'prop-types';

const projects = [
  {
    id: 1,
    title: "Luxury Villa Complex",
    location: "Greater Noida",
    completionDate: "2024",
    status: "In Progress",
    category: "residential",
    image: "/assets/i1.jpg",
    description: "Premium residential development featuring modern amenities and smart home technology.",
    highlights: [
      { icon: Building2, text: "25,000 sq ft" },
      { icon: Users, text: "50+ Units" },
      { icon: Target, text: "90% Complete" }
    ],
    achievements: [
      "Green Building Certified",
      "Smart Home Integration",
      "Premium Finishes"
    ]
  },
  {
    id: 2,
    title: "Commercial Plaza",
    location: "Noida",
    completionDate: "2023",
    status: "Completed",
    category: "commercial",
    image: "/assets/in4.jpg",
    description: "Modern commercial complex with retail spaces and office units.",
    highlights: [
      { icon: Building2, text: "40,000 sq ft" },
      { icon: Users, text: "30+ Shops" },
      { icon: Target, text: "100% Complete" }
    ],
    achievements: [
      "LEED Certified",
      "Modern Architecture",
      "Prime Location"
    ]
  },
  {
    id: 3,
    title: "Industrial Park",
    location: "Greater Noida",
    completionDate: "2024",
    status: "In Progress",
    category: "industrial",
    image: "/assets/ind1.jpg",
    description: "State-of-the-art industrial park with advanced infrastructure.",
    highlights: [
      { icon: Building2, text: "100,000 sq ft" },
      { icon: Users, text: "20+ Units" },
      { icon: Target, text: "75% Complete" }
    ],
    achievements: [
      "Eco-Friendly Design",
      "Advanced Security",
      "24/7 Power Backup"
    ]
  },
  {
    id: 4,
    title: "Smart Homes Project",
    location: "Noida",
    completionDate: "2023",
    status: "Completed",
    category: "residential",
    image: "/assets/i2.jpg",
    description: "Modern residential complex with smart home features and luxury amenities.",
    highlights: [
      { icon: Building2, text: "35,000 sq ft" },
      { icon: Users, text: "40+ Units" },
      { icon: Target, text: "100% Complete" }
    ],
    achievements: [
      "Smart Home Tech",
      "Luxury Amenities",
      "Sustainable Design"
    ]
  },
  {
    id: 5,
    title: "Tech Park",
    location: "Greater Noida",
    completionDate: "2024",
    status: "In Progress",
    category: "commercial",
    image: "/assets/in2.jpg",
    description: "Modern tech park with office spaces and recreational areas.",
    highlights: [
      { icon: Building2, text: "80,000 sq ft" },
      { icon: Users, text: "25+ Offices" },
      { icon: Target, text: "60% Complete" }
    ],
    achievements: [
      "Modern Design",
      "Green Spaces",
      "Tech Infrastructure"
    ]
  },
  {
    id: 6,
    title: "Warehouse Complex",
    location: "Noida",
    completionDate: "2023",
    status: "Completed",
    category: "industrial",
    image: "/assets/ind2.jpg",
    description: "Large-scale warehouse complex with modern logistics facilities.",
    highlights: [
      { icon: Building2, text: "150,000 sq ft" },
      { icon: Users, text: "10+ Units" },
      { icon: Target, text: "100% Complete" }
    ],
    achievements: [
      "Advanced Logistics",
      "24/7 Security",
      "Easy Access"
    ]
  }
];

const ProjectModal = ({ project, onClose }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center pt-20 pb-1 px-1 sm:pt-24 sm:pb-2 sm:px-2 md:pt-24 md:pb-4 md:px-8"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg sm:rounded-xl md:rounded-2xl overflow-hidden shadow-2xl w-full max-w-[95vw] sm:max-w-lg md:max-w-2xl lg:max-w-4xl xl:max-w-5xl relative max-h-[75vh] sm:max-h-[80vh] md:max-h-[85vh] lg:max-h-[90vh] overflow-y-auto"
      >
        <div className="flex flex-col md:flex-row">
          {/* Left Side - Image */}
          <div className="md:w-1/2 relative flex-shrink-0">
            <div className="relative h-32 sm:h-40 md:h-48 lg:h-full">
              <img
                src={project.image}
                alt={project.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
              
              {/* Project Title & Location */}
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <h3 className="text-3xl font-bold text-white mb-3">{project.title}</h3>
                <div className="flex flex-wrap gap-4 text-white/90">
                  <span className="flex items-center gap-2">
                    <MapPin className="w-5 h-5" />
                    {project.location}
                  </span>
                  <span className="flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    {project.completionDate}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Content */}
          <div className="md:w-1/2 p-2 sm:p-3 md:p-4 lg:p-6 xl:p-8 overflow-y-auto">
            {/* Status Badge */}
            <div className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4 md:mb-6
              ${project.status === "Completed" 
                ? "bg-green-100 text-green-700" 
                : "bg-amber-100 text-amber-700"}`}
            >
              {project.status}
            </div>

            {/* Description */}
            <p className="text-gray-700 text-sm sm:text-base md:text-lg leading-relaxed mb-4 sm:mb-6 md:mb-8">
              {project.description}
            </p>

            {/* Highlights */}
            <div className="grid grid-cols-2 gap-2 sm:gap-3 md:gap-4 mb-4 sm:mb-6 md:mb-8">
              {project.highlights.map((highlight, idx) => (
                <div key={idx} className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 md:p-4 rounded-xl bg-blue-50/50 border border-blue-100">
                  <highlight.icon className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-blue-600" />
                  <span className="text-gray-800 font-medium text-xs sm:text-sm md:text-base">{highlight.text}</span>
                </div>
              ))}
            </div>

            {/* Achievements */}
            <h4 className="text-lg font-semibold mb-4">Key Achievements</h4>
            <div className="grid grid-cols-1 gap-3">
              {project.achievements.map((achievement, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-3 p-4 rounded-xl bg-gray-50 border border-gray-100"
                >
                  <Award className="w-5 h-5 text-amber-500" />
                  <span className="text-gray-700">{achievement}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Close Button - Absolute positioned */}
          <button
            onClick={onClose}
            className="absolute top-1 right-1 sm:top-2 sm:right-2 md:top-4 md:right-4 p-1.5 sm:p-2 rounded-full bg-white/10 hover:bg-white/20 
              text-white backdrop-blur-sm transition-all duration-300 z-10"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

ProjectModal.propTypes = {
  project: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    location: PropTypes.string.isRequired,
    completionDate: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    highlights: PropTypes.arrayOf(PropTypes.shape({
      icon: PropTypes.elementType.isRequired,
      text: PropTypes.string.isRequired
    })).isRequired,
    achievements: PropTypes.arrayOf(PropTypes.string).isRequired
  }).isRequired,
  onClose: PropTypes.func.isRequired
};

const ProjectCard = ({ project }) => {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <motion.div
        layout
        onClick={() => setShowModal(true)}
        className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl 
          transform hover:-translate-y-2 transition-all duration-500 cursor-pointer"
      >
        {/* Project Image */}
        <div className="relative h-64 overflow-hidden">
          <img
            src={project.image}
            alt={project.title}
            className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
          
          {/* Status Badge */}
          <div className="absolute top-4 right-4">
            <div className={`px-3 py-1 rounded-full text-sm font-medium
              ${project.status === "Completed" 
                ? "bg-green-500/20 text-green-500 border border-green-500/20" 
                : "bg-blue-500/20 text-blue-500 border border-blue-500/20"} 
              backdrop-blur-md`}
            >
              {project.status}
            </div>
          </div>

          {/* Project Info */}
          <div className="absolute bottom-0 left-0 right-0 p-6">
            <h3 className="text-2xl font-bold text-white mb-2 transform group-hover:translate-x-2 
              transition-transform duration-300">{project.title}</h3>
            <div className="flex flex-wrap gap-3 text-white/90">
              <span className="flex items-center gap-1 text-sm">
                <MapPin className="w-4 h-4" />
                {project.location}
              </span>
              <span className="flex items-center gap-1 text-sm">
                <Calendar className="w-4 h-4" />
                {project.completionDate}
              </span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Project Modal */}
      <AnimatePresence mode="wait">
        {showModal && (
          <ProjectModal key="project-modal" project={project} onClose={() => setShowModal(false)} />
        )}
      </AnimatePresence>
    </>
  );
};

ProjectCard.propTypes = {
  project: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    location: PropTypes.string.isRequired,
    completionDate: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    highlights: PropTypes.arrayOf(PropTypes.shape({
      icon: PropTypes.elementType.isRequired,
      text: PropTypes.string.isRequired
    })).isRequired,
    achievements: PropTypes.arrayOf(PropTypes.string).isRequired
  }).isRequired
};

const Projects = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [visibleProjects, setVisibleProjects] = useState(6);

  const categories = [
    { id: 'all', name: 'All Projects' },
    { id: 'residential', name: 'Residential', icon: Building2 },
    { id: 'commercial', name: 'Commercial', icon: Building2 },
    { id: 'industrial', name: 'Industrial', icon: Building2 }
  ];

  const filteredProjects = activeCategory === 'all' 
    ? projects 
    : projects.filter(project => project.category === activeCategory);

  const displayedProjects = filteredProjects.slice(0, visibleProjects);

  const loadMore = () => {
    setVisibleProjects(prev => Math.min(prev + 3, filteredProjects.length));
  };

  return (
    <section id="projects" className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-100 py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold mb-6 bg-clip-text text-transparent 
              bg-gradient-to-r from-blue-600 to-blue-800"
          >
            Our Projects
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-gray-600 max-w-3xl mx-auto"
          >
            Explore our portfolio of exceptional construction projects
          </motion.p>
        </div>

        {/* Category Navigation */}
        <div className="flex flex-wrap justify-center gap-4 mb-16">
          {categories.map((category) => (
            <motion.button
              key={category.id}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setActiveCategory(category.id)}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl transition-all duration-300 
                ${activeCategory === category.id
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-white text-gray-600 hover:bg-gray-50 shadow'
                }`}
            >
              {category.icon && <category.icon className="w-5 h-5" />}
              {category.name}
            </motion.button>
          ))}
        </div>

        {/* Projects Grid */}
        <motion.div 
          layout
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          <AnimatePresence>
            {displayedProjects.map((project) => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </AnimatePresence>
        </motion.div>

        {/* Load More Button */}
        {visibleProjects < filteredProjects.length && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center mt-12"
          >
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={loadMore}
              className="px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white 
                rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Load More Projects
            </motion.button>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default Projects;
